import React, { forwardRef, Children } from 'react'
import { Portal } from 'react-portal'
import Rect from '@reach/rect'
import PropTypes from 'prop-types'
import cx from 'classnames'

import MenuItem from './MenuItem'
import MenuLink from './MenuLink'
import { Consumer } from './Menu'

import s from './MenuList.scss'


const focusableChildrenTypes = [ MenuItem, MenuLink ]

const isFocusableChildType = (child) => focusableChildrenTypes.includes(child.type)

const getFocusableMenuChildren = (children) => {
  const focusable = []

  Children.forEach(children, (child) => {
    if (isFocusableChildType(child)) {
      focusable.push(child)
    }
  })

  return focusable
}

const MenuListImpl = forwardRef((props, ref) => {
  const {
    refs, state, setState, children, onKeyDown, onBlur,
    onMouseEnter, onMouseLeave,
    handleListBlur, handleListKeyDown, handleListMouseEnter, handleListMouseLeave,
    ...rest
  } = props

  const focusableChildren = getFocusableMenuChildren(children)

  const setRef = (node) => {
    refs.list = node

    if (ref) {
      ref(node)
    }
  }

  const handleBlur = (event) => {
    handleListBlur(event)

    if (typeof onBlur === 'function') {
      onBlur(event)
    }
  }

  const handleKeyDown = (event) => {
    handleListKeyDown(event)

    if (typeof onKeyDown === 'function') {
      onKeyDown(event)
    }
  }

  const handleMouseEnter = (event) => {
    handleListMouseEnter(event)

    if (typeof onMouseEnter === 'function') {
      onMouseEnter(event)
    }
  }

  const handleMouseLeave = (event) => {
    handleListMouseLeave(event)

    if (typeof onMouseLeave === 'function') {
      onMouseLeave(event)
    }
  }

  return (
    <div
      {...rest}
      role="menu"
      aria-labelledby={state.buttonId}
      tabIndex="-1"
      ref={setRef}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {
        Children.map(children, (child) => {
          if (isFocusableChildType(child)) {
            let focusIndex = focusableChildren.indexOf(child)

            return React.cloneElement(child, {
              setState,
              state,
              index: focusIndex,
              _ref: (node) => (refs.items[focusIndex] = node),
            })
          }

          return Children.map(children, (child) => (
            React.cloneElement(child, {
              setState,
              state,
              _ref: (index) => (node) => (refs.items[index] = node),
            })
          ))
        })
      }
    </div>
  )
})

const MenuList = forwardRef((props, ref) => (
  <Consumer>
    {
      ({ refs, state, setState, getListStyles, handleListBlur, handleListKeyDown, handleListMouseEnter, handleListMouseLeave }) => (
        state.isOpen && (
          <Portal>
            <Rect>
              {
                ({ rect: listRect, ref: listRef }) => (
                  <div
                    ref={listRef}
                    style={getListStyles(listRect)}
                  >
                    <MenuListImpl
                      {...props}
                      className={cx(props.className, s.menuList)}
                      setState={setState}
                      state={state}
                      refs={refs}
                      ref={ref}
                      handleListBlur={handleListBlur}
                      handleListKeyDown={handleListKeyDown}
                      handleListMouseEnter={handleListMouseEnter}
                      handleListMouseLeave={handleListMouseLeave}
                    />
                  </div>
                )
              }
            </Rect>
          </Portal>
        )
      )
    }
  </Consumer>
))

MenuList.propTypes = {
  children: PropTypes.node,
}


export default MenuList
